import { logger } from '@disy/lib/logging';

import { DelegatingCompletionSource, LocalStorageCompletionSource } from 'ui/completion-input/completion-source';

import { errorAlert } from 'cadenza/alert';
import type { AddExternalLayersWizardOptions } from 'cadenza/map/add-external-layers-wizard/add-external-layers-wizard';
import type {
  ChangeExternalLayerCredentialsDialog,
  ChangeExternalLayerCredentialsDialogOptions,
} from 'cadenza/map/add-external-layers-wizard/change-external-layer-credentials-dialog';
import { getStore } from 'cadenza/store/cadenza-store';
import { getAllExternalLayerDatasources } from 'cadenza/workbook/api/external-layers-api';
import { getActiveWorksheetId, getWorkbookName } from 'cadenza/workbook/workbook-core-selectors.state';

import i18n from './add-external-layers-wizard.properties';

export async function showAddExternalLayersWizard(
  options: Pick<AddExternalLayersWizardOptions, 'viewId' | 'srsCode' | 'parentLayerId'>,
) {
  const [{ AddExternalLayersWizard }, dataSources] = await Promise.all([
    import('./add-external-layers-wizard'),
    getAllExternalLayerDatasources(options.srsCode).catch(() => []),
  ]);
  const workbookName = getWorkbookName(getStore().getState());
  const activeWorksheetId = getActiveWorksheetId(getStore().getState());
  const completionSource = new DelegatingCompletionSource(
    new LocalStorageCompletionSource(`GeoDataServiceUrls:${workbookName}:${activeWorksheetId}`),
  );
  try {
    const wizard = new AddExternalLayersWizard(dataSources, { completionSource, ...options });
    await wizard.connectAndShow({ hidePreviousModals: false });
  } catch (error) {
    logger.error('Failed to add external layers.', error);
    errorAlert(i18n('error.dialog.title'), i18n('error.dialog.message'));
  }
}

export function showChangeExternalLayerCredentialsDialog(options: ChangeExternalLayerCredentialsDialogOptions) {
  let dialog: ChangeExternalLayerCredentialsDialog;
  return import('./change-external-layer-credentials-dialog')
    .then(({ ChangeExternalLayerCredentialsDialog }) => {
      dialog = new ChangeExternalLayerCredentialsDialog(options);
      document.body.appendChild(dialog);
      return dialog;
    })
    .then((wizard) => wizard.showModal())
    .then(() => dialog.remove())
    .catch((error) => {
      logger.error('Failed to change external layer credentials.', error);
      errorAlert(i18n('error.dialog.title'), i18n('error.dialog.message'));
    });
}
